import type {
  ClientSelection,
  ClientSnapshot,
  MantaClient,
  MantaPatient,
} from '@/types';

export function clientDisplayName(
  client: Pick<
    MantaClient,
    'display_name' | 'name' | 'first_name' | 'last_name'
  >,
): string {
  return (
    client.display_name?.trim() ||
    client.name?.trim() ||
    [client.first_name, client.last_name].filter(Boolean).join(' ') ||
    ''
  );
}

export function animalsOf(client: MantaClient): MantaPatient[] {
  return (client.patientLinks ?? [])
    .map((link) => link.patient)
    .filter((patient): patient is MantaPatient => !!patient);
}

export function animalNames(
  snapshot: ClientSnapshot | null | undefined,
): string {
  return (snapshot?.patients ?? [])
    .map((p) => p.name)
    .filter(Boolean)
    .join(', ');
}

export function clientSelectionOf(
  client: MantaClient,
  patients: MantaPatient[],
): ClientSelection {
  return {
    clientId: client.id,
    patientIds: patients.map((p) => p.id),
    snapshot: {
      client_name: clientDisplayName(client) || undefined,
      patients: patients.map((p) => ({
        id: p.id,
        name: p.name ?? undefined,
        species: p.species ?? undefined,
        breed: p.breed ?? undefined,
      })),
    },
  };
}

export function initialsOf(name: string | null | undefined): string {
  return (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

/** PMS-synced clients are overwritten by the next sync, so only ours are editable. */
export function isClientEditable(client: Pick<MantaClient, 'managed_by'>) {
  return client.managed_by === 'INTERNAL';
}

/** Animals with an `external_id` come from the PMS and are read-only. */
export function isPatientEditable(patient: Pick<MantaPatient, 'external_id'>) {
  return !patient.external_id;
}

/** First/last name for the edit form, splitting a legacy free-text name. */
export function clientNameParts(client: MantaClient): {
  firstName: string;
  lastName: string;
} {
  if (client.first_name || client.last_name) {
    return {
      firstName: client.first_name?.trim() ?? '',
      lastName: client.last_name?.trim() ?? '',
    };
  }
  return splitFullName(clientDisplayName(client));
}

/**
 * Splits a typed name into first/last name for prefilling the create form:
 * the last word is the last name, anything before it the first name.
 */
export function splitFullName(full: string): {
  firstName: string;
  lastName: string;
} {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { firstName: '', lastName: parts[0] ?? '' };
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
}
